import IconButton from "components/atoms/button/IconButton";
import Form from "components/atoms/form/Form";
import Input from "components/atoms/input/Input";
import Label from "components/atoms/input/input-label/InputLabel";
import { FC, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { usePermissions } from "lib/hooks/shared/usePermissions";
import Creatable from "react-select/creatable";
import { customStyles } from "lib/helpers/configs/customStyles";
import { useAllSources } from "lib/hooks/queries/Source/useAllSources";
import Button from "components/atoms/button/Button";
import Icon from "components/atoms/icon/Icon";
import { IconType } from "lib/helpers/constants/iconTypes";
import { useTranslation } from "react-i18next";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import { useLeadersAndUsersByPermission } from "lib/hooks/queries/User/useLeadersAndUsersByPermission";

export interface IStep1Props {
  setStep: (args: number) => void;
  handleFormValuesChange: (step: number, values: any) => void;
  FormValues: Array<string>;
  themeColors: {
    primary: string;
  };
}

const Step1: FC<IStep1Props> = ({
  setStep,
  handleFormValuesChange,
  FormValues,
  themeColors
}) => {

  const sources = useAllSources();
  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    control
  } = useForm();



  const hasPermissionToAssignLeads = usePermissions(['assign_assigned_leads_assign']);



  const onSubmit = (data: any) => {

    setStep(2);
    const FullUser = fetchedUsers.find((user: any) => user.value === data.assigned_to);

    const finalData = {
      name: data.name,
      assigned_to: FullUser ? { id: FullUser.value, in_team: FullUser?.team || null } : undefined,
      source_id: data.source_id || undefined
    }

    const filteredData = Object.fromEntries(
      Object.entries(finalData).filter(([_, value]) => value !== undefined)
    );
  
    handleFormValuesChange(1, filteredData);
  };


  const { data } = useLeadersAndUsersByPermission({
    permissions_for_assign: 'assign_assigned_leads_assign',
    permissions_for_assigment: 'leads_assigment_assign',
  })

  const fetchedUsers = useMemo(() => {
    if (!data) return [];

    const leadersOrAdmins = (data as any).usersInTeamAsLeadersOrAdmin.map((user: any) => ({
      label: user.name + " " + user.last_name + " (" + user.team_name + '/' + user.in_team + ")",
      value: user.id,
      team: user.team_name,
    }));

    const users = (data as any).usersExpectLeadersOrAdmin.map((user: any) => ({
      label: user.name + " " + user.last_name,
      value: user.id,
    }));

    return [...leadersOrAdmins, ...users];

  }, [data])

  const { t } = useTranslation();

  return (
    <Form
      className="mb-0 flex flex-col gap-[13px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        <div>
          <Label>{t('Campaign Name')}*</Label>
        </div>
        <div>
          <Input
            {...register("name", { required: t("Name is required") })}
            error={errors.name}
            placeholder={t("Enter campaign name")}
            defaultValue={(FormValues as any).name}
            classes={{ errorClassName: '!bottom-[-14px]' }}
          />{" "}
        </div>
      </div>
      <ConditionalRender condition={hasPermissionToAssignLeads}>
        <div className="relative">
          <div>
            <Label>{t('Assigned to')}</Label>
          </div>
          <div>
            <Controller
              name="assigned_to"
              control={control}
              render={({ field }) => (
                <Creatable<any, false>
                  {...field}
                  placeholder={t("Choose a user")}
                  options={fetchedUsers}
                  value={
                    fetchedUsers.find((option) => option.value === field.value) || ""
                  }
                  isValidNewOption={() => false}
                  className="cursor-pointer font-inter-regular react-select-container"
                  classNamePrefix="react-select"
                  onChange={(newValue: any, actionMeta: any) => {
                    if (
                      actionMeta.action === "select-option" &&
                      newValue?.value
                    ) {
                      field.onChange(newValue.value);
                    }
                  }}
                  styles={customStyles}
                  menuPosition={"fixed"}
                  menuPortalTarget={document.body}
                />
              )}
            />
            {errors.assigned_to?.message && (
              <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-14px] 2xl:bottom-[-14px]  left-[2px]">
                <>{errors.assigned_to?.message}*</>
              </div>
            )}

          </div>
          <div className="flex flex-col gap-[15px] 2xl:gap-[24px]"></div>
        </div>
      </ConditionalRender>
      <div>
        <div className="relative">
          <Label text={t("Source")} />
          <Controller
            name="source_id"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t("Choose source")}
                options={sources?.data?.sources?.map?.((source: any) => ({
                  label: source.name ?? "",
                  value: source.id
                }))}
                value={
                  field?.value
                    ? {
                      value: field?.value,
                      label:
                        (
                          sources?.data?.sources?.find?.(
                            (item: any) => item.id === field.value
                          ) as { name: string } | undefined
                        )?.name || field.value
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(newValue: any, actionMeta: any) => {
                  if (
                    actionMeta.action === "select-option" &&
                    newValue?.value
                  ) {
                    field.onChange(newValue.value);
                  }
                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
        </div>
        <ConditionalRender condition={usePermissions(['source_management_create'])}>
          <Button type="button" onClick={() => setStep(7)} className="text-[--theme] flex items-center gap-1 justify-end ml-auto font-inter-medium text-[13px] mt-2">
            <Icon iconType={IconType.INCRASE_ICON} color={themeColors.primary} />
            {t('Add Source')}
          </Button>
        </ConditionalRender>
      </div>

      <div className="flex gap-5 justify-end">
        <div>
          <IconButton
            secondary={true}
            type="button"
            onClick={() =>
              reset({
                name: "",
                assigned_to: ""
              })
            }
            className={`w-full min-w-[120px] `}
          >
            {t('Reset')}
          </IconButton>
        </div>
        <div>
          <IconButton
            type="submit"
            className={`w-full min-w-[120px]  bg-[${themeColors.primary}] border-[${themeColors.primary}]`}
          >
            {t('Continue')}
          </IconButton>
        </div>
      </div>
    </Form>
  );
};

export default Step1;
